/**
 * EditFieldModal — Modal for editing a single report text field.
 * Uses TipTap (StarterKit) for the editor surface. Stores MARKDOWN on save
 * (htmlToMarkdown) so bold/italic/lists survive — getText() used to flatten
 * them away. On load, stored markdown emphasis is converted back to HTML
 * (mdInlineToHtml) so the editor shows formatting, not literal asterisks.
 * The report renders these fields through <Markdown> (EditableText markdown
 * prop). Markdown is a superset of plain text, so pre-existing plain edits
 * and AI-generated markdown fallbacks both render unchanged.
 */
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { X, Check, Loader2, RotateCcw, Bold, Italic, List, ListOrdered, Quote, Link as LinkIcon } from 'lucide-react';
import ModalWrapper from '../ModalWrapper';
import { useReportEdits } from './ReportEditsContext';

const Box = styled.div`
  background: #fff;
  border-radius: 6px;
  width: min(720px, 92vw);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
  font-family: inherit;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid #e5e7eb;
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Label = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #111;
`;

const FieldKey = styled.div`
  font-size: 0.7rem;
  color: #9ca3af;
  font-family: 'JetBrains Mono', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 6px;
  cursor: pointer;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover { background: #f3f4f6; color: #111; }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 18px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
`;

const ToolBtn = styled.button`
  background: ${p => (p.$active ? '#ede9fe' : 'transparent')};
  color: ${p => (p.$active ? '#5b21b6' : '#374151')};
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  &:hover { background: #ede9fe; color: #5b21b6; }
`;

const EditorWrap = styled.div`
  padding: 18px 22px;
  overflow-y: auto;
  flex: 1;

  .ProseMirror {
    outline: none;
    min-height: 220px;
    font-size: 1rem;
    color: #1f2937;
    line-height: 1.65;
  }
  .ProseMirror p { margin: 0 0 12px; }
  .ProseMirror p:last-child { margin-bottom: 0; }
  .ProseMirror h1, .ProseMirror h2, .ProseMirror h3 { font-weight: 700; color: #000; margin: 16px 0 8px; }
  .ProseMirror ul, .ProseMirror ol { padding-left: 1.4em; margin: 0 0 12px; }
  .ProseMirror blockquote { border-left: 3px solid #d1d5db; padding-left: 12px; margin: 8px 0; color: #4b5563; font-style: italic; }
  .ProseMirror code { background: #f3f4f6; padding: 1px 4px; border-radius: 3px; font-size: 0.9em; }
  .ProseMirror:focus { outline: none; }
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 22px 16px;
  border-top: 1px solid #f3f4f6;
  background: #fafbfc;
`;

const LinkLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4b5563;
  flex-shrink: 0;
`;

const LinkInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  font-size: 0.85rem;
  font-family: 'JetBrains Mono', monospace;
  color: #1f2937;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  &:focus { outline: 2px solid #7c3aed; outline-offset: -1px; border-color: #7c3aed; }
  &::placeholder { color: #9ca3af; }
`;

const FootRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 22px;
  border-top: 1px solid #e5e7eb;
  gap: 12px;
`;

const Meta = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid transparent;
  ${p => p.$primary ? `
    background: #7c3aed;
    color: #fff;
    &:hover { background: #5b21b6; }
    &:disabled { opacity: 0.6; cursor: progress; }
  ` : `
    background: #fff;
    color: #374151;
    border-color: #e5e7eb;
    &:hover { background: #f9fafb; color: #111; }
  `}
  svg { ${p => p.$saving ? 'animation: spin 1s linear infinite;' : ''} }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

function ToolbarButton({ active, onClick, title, children }) {
  return (
    <ToolBtn type="button" $active={active} onClick={onClick} title={title} aria-label={title}>
      {children}
    </ToolBtn>
  );
}

export default function EditFieldModal({
  open,
  onClose,
  fieldKey,
  label,
  fallback,
  multiline = true,
  // Optional sibling field key for a URL/hyperlink. When set, the modal
  // also renders a URL input below the editor and saves it atomically
  // alongside the main text. Used by the Tietopohja source list so the
  // title edit modal also exposes the link.
  linkFieldKey,
  linkFallback = '',
  linkLabel = 'Linkki',
}) {
  const { save, get, isEdited, savingKeys } = useReportEdits();
  const initialText = get(fieldKey, fallback);
  const initialLink = linkFieldKey ? get(linkFieldKey, linkFallback) : '';
  const [linkValue, setLinkValue] = React.useState(initialLink);
  React.useEffect(() => { if (open) setLinkValue(initialLink); }, [open, initialLink]);

  // Build editor content from plain text. Split by \n into paragraphs so TipTap
  // renders breaks the way the user typed them.
  const initialContent = React.useMemo(() => {
    const paras = (initialText || '').split(/\n+/).filter(Boolean);
    if (paras.length === 0) return '<p></p>';
    return paras.map(p => `<p>${mdInlineToHtml(p)}</p>`).join('');
  }, [initialText]);

  const editor = useEditor({
    extensions: [StarterKit.configure({
      heading: multiline ? { levels: [2, 3] } : false,
      bulletList: multiline ? {} : false,
      orderedList: multiline ? {} : false,
      blockquote: multiline ? {} : false,
      codeBlock: false,
      horizontalRule: false,
    })],
    content: initialContent,
    editorProps: { attributes: { spellcheck: 'true' } },
  }, [open, fieldKey]);

  // Reset content if modal is reopened with different field
  useEffect(() => {
    if (editor && open) {
      editor.commands.setContent(initialContent);
      // Focus the editor when the modal opens
      setTimeout(() => editor.commands.focus('end'), 50);
    }
  }, [editor, open, initialContent]);

  if (!open) return null;

  const saving = savingKeys.has(fieldKey);
  const edited = isEdited(fieldKey);

  const handleSave = async () => {
    if (!editor) return;
    const text = htmlToMarkdown(editor.getHTML()).trim();
    try {
      // When the modal also drives a sibling link field, save both. Run
      // them in parallel — they're independent overlay keys.
      await Promise.all([
        save(fieldKey, text),
        linkFieldKey ? save(linkFieldKey, (linkValue || '').trim()) : Promise.resolve(),
      ]);
      onClose();
    } catch (err) {
      console.error('[edit-field-modal] save failed', err);
    }
  };

  const handleReset = () => {
    if (editor) {
      const fallbackContent = (fallback || '')
        .split(/\n+/)
        .filter(Boolean)
        .map(p => `<p>${mdInlineToHtml(p)}</p>`)
        .join('') || '<p></p>';
      editor.commands.setContent(fallbackContent);
    }
  };

  return (
    <ModalWrapper visible={open} onClose={onClose} showCloseButton={false} opacity={0.5} blur={2}>
      <Box>
        <Head>
          <Title>
            <Label>{label || 'Muokkaa kenttää'}</Label>
            <FieldKey>{fieldKey}</FieldKey>
          </Title>
          <CloseBtn onClick={onClose} aria-label="Sulje"><X size={16} /></CloseBtn>
        </Head>

        {editor && multiline && (
          <Toolbar>
            <ToolbarButton
              active={editor.isActive('bold')}
              onClick={() => editor.chain().focus().toggleBold().run()}
              title="Lihavointi (Cmd+B)"
            >
              <Bold size={14} />
            </ToolbarButton>
            <ToolbarButton
              active={editor.isActive('italic')}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              title="Kursivointi (Cmd+I)"
            >
              <Italic size={14} />
            </ToolbarButton>
            <ToolbarButton
              active={editor.isActive('bulletList')}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              title="Luettelo"
            >
              <List size={14} />
            </ToolbarButton>
            <ToolbarButton
              active={editor.isActive('orderedList')}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              title="Numeroitu luettelo"
            >
              <ListOrdered size={14} />
            </ToolbarButton>
            <ToolbarButton
              active={editor.isActive('blockquote')}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              title="Lainaus"
            >
              <Quote size={14} />
            </ToolbarButton>
          </Toolbar>
        )}

        <EditorWrap>
          <EditorContent editor={editor} />
        </EditorWrap>

        {linkFieldKey && (
          <LinkRow>
            <LinkLabel htmlFor={`${fieldKey}--link`}>
              <LinkIcon size={13} /> {linkLabel}
            </LinkLabel>
            <LinkInput
              id={`${fieldKey}--link`}
              type="url"
              value={linkValue}
              onChange={(e) => setLinkValue(e.target.value)}
              placeholder="https://…"
              autoComplete="off"
              spellCheck={false}
            />
          </LinkRow>
        )}

        <FootRow>
          <Meta>
            {edited && (
              <span title="Tämä kenttä on muokattu aiemmin — voit palauttaa AI:n alkuperäisen tekstin">
                AI:n alkuperäinen on saatavilla
              </span>
            )}
          </Meta>
          <Actions>
            {edited && (
              <Btn type="button" onClick={handleReset} title="Palauta AI:n alkuperäinen teksti">
                <RotateCcw size={14} /> Palauta AI:n teksti
              </Btn>
            )}
            <Btn type="button" onClick={onClose}>Peruuta</Btn>
            <Btn $primary $saving={saving} onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 size={14} /> : <Check size={14} />}
              {saving ? 'Tallennetaan' : 'Tallenna'}
            </Btn>
          </Actions>
        </FootRow>
      </Box>
    </ModalWrapper>
  );
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Serialize the TipTap HTML to markdown on save, so bold/italic/lists survive
// (getText() used to flatten them away). Covers exactly what the toolbar can
// produce: strong, em, strike, links, bullet/ordered lists, blockquote,
// headings, paragraphs. The report renders this markdown via <Markdown>.
function htmlToMarkdown(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const inline = (node) => {
    let out = '';
    node.childNodes.forEach((n) => {
      if (n.nodeType === 3) { out += n.textContent; return; }
      if (n.nodeType !== 1) return;
      const tag = n.tagName.toLowerCase();
      const inner = inline(n);
      if (tag === 'strong' || tag === 'b') out += `**${inner}**`;
      else if (tag === 'em' || tag === 'i') out += `*${inner}*`;
      else if (tag === 's' || tag === 'del' || tag === 'strike') out += `~~${inner}~~`;
      else if (tag === 'br') out += '\n';
      else if (tag === 'a') out += `[${inner}](${n.getAttribute('href') || ''})`;
      else out += inner;
    });
    return out;
  };
  const blocks = [];
  doc.body.childNodes.forEach((node) => {
    if (node.nodeType === 3) { const t = node.textContent.trim(); if (t) blocks.push(t); return; }
    if (node.nodeType !== 1) return;
    const tag = node.tagName.toLowerCase();
    const items = (t) => [...node.querySelectorAll(':scope > li')].map((li, i) => t(i) + inline(li).trim());
    if (/^h[1-6]$/.test(tag)) blocks.push(`${'#'.repeat(Number(tag[1]))} ${inline(node).trim()}`);
    else if (tag === 'ul') blocks.push(items(() => '- ').join('\n'));
    else if (tag === 'ol') blocks.push(items((i) => `${i + 1}. `).join('\n'));
    else if (tag === 'blockquote') blocks.push(`> ${inline(node).trim().replace(/\n/g, '\n> ')}`);
    else blocks.push(inline(node).trim());
  });
  return blocks.filter((b) => b !== '').join('\n\n');
}

// Inverse for loading: turn stored markdown back into HTML so the editor shows
// bold/italic instead of literal `**asterisks**`. Inline-only (bold/italic) —
// enough for round-tripping the emphasis the toolbar produces; list/quote
// markers reload as literal text but still render correctly in the report.
function mdInlineToHtml(text) {
  return escapeHtml(text)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/(^|[^_])_([^_\n]+)_/g, '$1<em>$2</em>');
}
